import { useState, useEffect } from 'react';
import api from '../services/api';

interface AuditLogEntry {
  id: string;
  user_id: string | null;
  user_email: string | null;
  user_name: string | null;
  timestamp: string;
  action_type: string;
  entity_type: string;
  entity_id: string | null;
  entity_name: string | null;
  old_value: Record<string, any> | null;
  new_value: Record<string, any> | null;
  changes_summary: string | null;
  ip_address: string | null;
}

interface AuditLogStats {
  total: number;
  today: number;
  thisWeek: number;
  byAction: Record<string, number>;
}

const ACTION_COLORS: Record<string, string> = {
  create: 'bg-green-100 text-green-800',
  update: 'bg-blue-100 text-blue-800',
  delete: 'bg-red-100 text-red-800',
  login: 'bg-purple-100 text-purple-800',
  logout: 'bg-gray-100 text-gray-800',
  export: 'bg-yellow-100 text-yellow-800',
  permission_change: 'bg-orange-100 text-orange-800',
  status_change: 'bg-indigo-100 text-indigo-800',
  bulk_action: 'bg-pink-100 text-pink-800',
};

const ACTION_LABELS: Record<string, string> = {
  create: 'Create',
  update: 'Update',
  delete: 'Delete',
  login: 'Login',
  logout: 'Logout',
  export: 'Export',
  permission_change: 'Permission Change',
  status_change: 'Status Change',
  bulk_action: 'Bulk Action',
};

// Heavy logging helper
const log = (action: string, data?: any) => {
  console.log(`[AUDIT_LOG_PAGE] ${action}`, data !== undefined ? data : '');
};

const logError = (action: string, error: any) => {
  console.error(`[AUDIT_LOG_PAGE] ERROR - ${action}:`, error);
  console.error(`[AUDIT_LOG_PAGE] Error type:`, typeof error);
  console.error(`[AUDIT_LOG_PAGE] Error message:`, error?.message);
  console.error(`[AUDIT_LOG_PAGE] Error stack:`, error?.stack);
  if (error?.response) {
    console.error(`[AUDIT_LOG_PAGE] Response data:`, error.response.data);
  }
};

export default function AuditLogPage() {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [stats, setStats] = useState<AuditLogStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [actionType, setActionType] = useState('');
  const [entityType, setEntityType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [entityTypes, setEntityTypes] = useState<string[]>([]);
  const [selectedEntry, setSelectedEntry] = useState<AuditLogEntry | null>(null);
  const [exporting, setExporting] = useState(false);

  log('Component mounted');

  useEffect(() => {
    log('Initial useEffect - loading entity types and stats');
    loadEntityTypes();
    loadStats();
  }, []);

  useEffect(() => {
    log('Filter useEffect triggered', { search, actionType, entityType, startDate, endDate, page });
    loadEntries();
  }, [search, actionType, entityType, startDate, endDate, page]);

  const loadEntityTypes = async () => {
    log('loadEntityTypes - START');
    try {
      const response = await api.get<string[]>('/audit-log/entity-types');
      log('loadEntityTypes - RAW RESPONSE:', response);
      log('loadEntityTypes - Response type:', typeof response);
      log('loadEntityTypes - Is array:', Array.isArray(response));

      if (!Array.isArray(response)) {
        throw new Error(`Expected array, got ${typeof response}: ${JSON.stringify(response)}`);
      }

      setEntityTypes(response);
      log('loadEntityTypes - SUCCESS, count:', response.length);
    } catch (error) {
      logError('loadEntityTypes', error);
    }
  };

  const loadStats = async () => {
    log('loadStats - START');
    try {
      const response = await api.get<AuditLogStats>('/audit-log/stats');
      log('loadStats - RAW RESPONSE:', response);
      log('loadStats - Response type:', typeof response);
      log('loadStats - Has total:', 'total' in (response || {}));

      if (!response || typeof response !== 'object') {
        throw new Error(`Expected object, got ${typeof response}: ${JSON.stringify(response)}`);
      }

      setStats(response);
      log('loadStats - SUCCESS');
    } catch (error) {
      logError('loadStats', error);
    }
  };

  const loadEntries = async () => {
    log('loadEntries - START');
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (actionType) params.append('action_type', actionType);
      if (entityType) params.append('entity_type', entityType);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      params.append('page', page.toString());
      params.append('limit', '50');

      const url = `/audit-log?${params.toString()}`;
      log('loadEntries - Fetching URL:', url);

      const response = await api.get<{ data: AuditLogEntry[]; totalPages: number }>(url);

      log('loadEntries - RAW RESPONSE:', response);
      log('loadEntries - Response type:', typeof response);
      log('loadEntries - Response keys:', response ? Object.keys(response) : 'null/undefined');
      log('loadEntries - Has data prop:', response && 'data' in response);
      log('loadEntries - Has totalPages prop:', response && 'totalPages' in response);

      if (!response) {
        throw new Error('Response is null/undefined');
      }

      if (!('data' in response)) {
        throw new Error(`Response missing 'data' property. Got: ${JSON.stringify(response)}`);
      }

      if (!Array.isArray(response.data)) {
        throw new Error(`response.data is not an array. Got ${typeof response.data}: ${JSON.stringify(response.data)}`);
      }

      log('loadEntries - Setting entries, count:', response.data.length);
      log('loadEntries - Setting totalPages:', response.totalPages);

      setEntries(response.data);
      setTotalPages(response.totalPages || 1);
      log('loadEntries - SUCCESS');
    } catch (error: any) {
      logError('loadEntries', error);
      setError(`Failed to load audit log: ${error?.message || 'Unknown error'}`);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    log('handleExport - START');
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (actionType) params.append('action_type', actionType);
      if (entityType) params.append('entity_type', entityType);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      const token = localStorage.getItem('admin_auth_token');
      const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

      const response = await fetch(`${API_BASE}/audit-log/export?${params.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit-log-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      log('handleExport - SUCCESS');
    } catch (error) {
      logError('handleExport', error);
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleString();
    } catch (e) {
      return dateStr;
    }
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  return (
    <div className="p-6">
      {/* Error Banner */}
      {error && (
        <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
          <strong>Error:</strong> {error}
          <button
            onClick={() => { setError(null); loadEntries(); }}
            className="ml-4 underline"
          >
            Retry
          </button>
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Audit Log</h1>
          <p className="text-gray-600 dark:text-gray-400">Track all system changes and user actions</p>
        </div>
        <button
          onClick={handleExport}
          disabled={exporting}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
        >
          {exporting ? (
            <>
              <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Exporting...
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export CSV
            </>
          )}
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-sm text-gray-500 dark:text-gray-400">Total Records</div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total?.toLocaleString() || 0}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-sm text-gray-500 dark:text-gray-400">Today</div>
            <div className="text-2xl font-bold text-blue-600">{stats.today?.toLocaleString() || 0}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-sm text-gray-500 dark:text-gray-400">This Week</div>
            <div className="text-2xl font-bold text-green-600">{stats.thisWeek?.toLocaleString() || 0}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="text-sm text-gray-500 dark:text-gray-400">Deletes Today</div>
            <div className="text-2xl font-bold text-red-600">{stats.byAction?.delete || 0}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Search</label>
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Action Type</label>
            <select
              value={actionType}
              onChange={(e) => { setActionType(e.target.value); setPage(1); }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">All Actions</option>
              {Object.entries(ACTION_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Entity Type</label>
            <select
              value={entityType}
              onChange={(e) => { setEntityType(e.target.value); setPage(1); }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">All Entities</option>
              {entityTypes.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <svg className="animate-spin h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            No audit log entries found
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Timestamp</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Action</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Entity</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Changes</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">IP</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase"></th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {entries.map((entry) => (
                  <tr
                    key={entry.id}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${entry.action_type === 'delete' ? 'bg-red-50 dark:bg-red-900/20' : ''}`}
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(entry.timestamp)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {entry.user_name || 'System'}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {entry.user_email || '-'}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${ACTION_COLORS[entry.action_type] || 'bg-gray-100 text-gray-800'}`}>
                        {ACTION_LABELS[entry.action_type] || entry.action_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {entry.entity_type}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {entry.entity_name || entry.entity_id || '-'}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-700 dark:text-gray-300 max-w-xs truncate">
                        {entry.changes_summary || '-'}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {entry.ip_address || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <button
                        onClick={() => setSelectedEntry(entry)}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Page {page} of {totalPages}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Details Modal */}
      {selectedEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Audit Log Details</h2>
              <button
                onClick={() => setSelectedEntry(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Timestamp</label>
                  <div className="text-gray-900 dark:text-white">{formatDate(selectedEntry.timestamp)}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">User</label>
                  <div className="text-gray-900 dark:text-white">{selectedEntry.user_name || 'System'}</div>
                  <div className="text-sm text-gray-500">{selectedEntry.user_email}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Action</label>
                  <div>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${ACTION_COLORS[selectedEntry.action_type] || 'bg-gray-100 text-gray-800'}`}>
                      {ACTION_LABELS[selectedEntry.action_type] || selectedEntry.action_type}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Entity</label>
                  <div className="text-gray-900 dark:text-white">{selectedEntry.entity_type}</div>
                  <div className="text-sm text-gray-500">{selectedEntry.entity_name || selectedEntry.entity_id}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">IP Address</label>
                  <div className="text-gray-900 dark:text-white">{selectedEntry.ip_address || '-'}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Entity ID</label>
                  <div className="text-gray-900 dark:text-white font-mono text-sm">{selectedEntry.entity_id || '-'}</div>
                </div>
              </div>

              {selectedEntry.changes_summary && (
                <div className="mb-6">
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Changes Summary</label>
                  <div className="mt-1 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-gray-900 dark:text-white">
                    {selectedEntry.changes_summary}
                  </div>
                </div>
              )}

              {selectedEntry.old_value && (
                <div className="mb-6">
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Previous Value</label>
                  <pre className="mt-1 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-sm overflow-x-auto text-gray-900 dark:text-white">
                    {formatValue(selectedEntry.old_value)}
                  </pre>
                </div>
              )}

              {selectedEntry.new_value && (
                <div>
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">New Value</label>
                  <pre className="mt-1 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-sm overflow-x-auto text-gray-900 dark:text-white">
                    {formatValue(selectedEntry.new_value)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
